import React, { useState, useEffect } from 'react'
// import { useApolloClient, gql } from '@apollo/client'
import { Box, styled, useMediaQuery, useTheme } from '@mui/material'
import { bool, string } from 'prop-types'
import Radar from 'radar-sdk-js'
import 'radar-sdk-js/dist/radar.css'
import { getRegionZone } from '@helpers/geo-location'
import useStoreLocatorContext from '@context/storeLocatorContext/useStoreLocatorContext'
import useIsFirstRender from '@hooks/useIsFirstRender'
import RadarCurrentMarker from './RadarCurrentMarker'
import RadarStoreMarkers from './RadarStoreMarkers'
import RadarSearchAreaButton from './RadarSearchAreaButton'

const RadarMapContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  height: '100%',
  width: '100%',
  '& .selected-marker > svg': {
    color: `${theme.palette.error.main} !important`,
  },
  '& .maplibregl-popup-close-button': {
    fontSize: '24px',
    paddingRight: '4px',
  },
}))

const DEFAULT_COORDS = {
  latitude: 28.538336,
  longitude: -81.379234,
}

export const getCenterPosition = myStore => {
  if (myStore) {
    const lat = myStore?.storeLocation?.latLng?.lat
    const lng = myStore?.storeLocation?.latLng?.lng
    if (lat && lng) {
      return {
        lat,
        lng,
      }
    }
  }

  return {
    lat: DEFAULT_COORDS.latitude,
    lng: DEFAULT_COORDS.longitude,
  }
}

const RadarMap = ({ variant, includeCurrentMarker }) => {
  const [isInitialized, setIsInitialized] = useState(false)
  // const apolloClient = useApolloClient()
  const { zip } = getRegionZone()
  const isFirstRender = useIsFirstRender()
  const myStoreInContext = useStoreLocatorContext(state => state.myStore)
  const selectedStore = useStoreLocatorContext(state => state.selectedStore)
  const setCurrentPosition = useStoreLocatorContext(state => state.setCurrentPosition)
  const setMapInstance = useStoreLocatorContext(state => state.setMapInstance)
  const mapInstance = useStoreLocatorContext(state => state.mapInstance)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // This loads the map and sets the initial center position
  useEffect(() => {
    if (isInitialized || isFirstRender || !myStoreInContext) return
    Radar.initialize(process.env.GATSBY_RADAR_MAPS_KEY ?? '')
    const center = getCenterPosition(myStoreInContext)
    const initialMap = Radar.ui.map({
      container: variant,
      style: 'radar-default-v1',
      center: [center.lng, center.lat],
      zoom: 9,
    })
    if (center.lat && center.lng) setCurrentPosition({ lat: center.lat, lng: center.lng })
    setMapInstance(initialMap)
    setIsInitialized(true)
  }, [setMapInstance, myStoreInContext, zip, setCurrentPosition, isInitialized, variant, isFirstRender])

  // This pans to the selected store when it is selected
  useEffect(() => {
    if (!mapInstance?.loaded || !selectedStore) return

    mapInstance.panTo([selectedStore.lng, selectedStore.lat], {
      animate: true,
      duration: 800,
      essential: true,
      offset: isMobile ? [0, -100] : [0, 0],
    })
  }, [isMobile, mapInstance, selectedStore])

  return (
    <>
      <RadarMapContainer id="map-container">
        <Box id={variant} position="absolute" width="100%" height="100%" />
        <RadarSearchAreaButton />
      </RadarMapContainer>
      <RadarStoreMarkers variant={variant} />
      {includeCurrentMarker && <RadarCurrentMarker />}
    </>
  )
}

export default RadarMap

RadarMap.propTypes = {
  variant: string,
  includeCurrentMarker: bool,
}
